import React from 'react';
import styled from 'styled-components';

const CrisisContainer = styled.section`
  id: crisis;
  padding: ${({ theme }) => `${theme.spacing['3xl']} ${theme.spacing.lg}`};
  background: ${({ theme }) => theme.colors.accent.gray};
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing['2xl']} ${theme.spacing.md}`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.sm}`};
  }
`;

const CrisisContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const CrisisTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary.main};
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  line-height: 1.2;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
  }
`;

const CrisisSubtitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: 300;
  color: ${({ theme }) => theme.colors.secondary.main};
  margin: 0 0 ${({ theme }) => theme.spacing['2xl']} 0;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

const StatisticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  margin-top: ${({ theme }) => theme.spacing['2xl']};
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  text-align: center;
  transition: ${({ theme }) => theme.transitions.smooth};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const StatNumber = styled.div`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes['5xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary.main};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  line-height: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

const StatLabel = styled.h3`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.base};
  }
`;

const StatDescription = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 300;
  color: ${({ theme }) => theme.colors.secondary.main};
  margin: 0;
  line-height: 1.4;
`;

const UrgencyBanner = styled.div`
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.accent.primary} 0%, ${({ theme }) => theme.colors.accent.secondary} 100%);
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: 8px;
  margin-top: ${({ theme }) => theme.spacing['2xl']};
  text-align: center;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  border: 2px solid ${({ theme }) => `${theme.colors.white}19`};
`;

const UrgencyText = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.base};
  }
`;

const CrisisResponseSection = () => {
  console.log('CrisisResponseSection updated - contrast and colors fixed');
  
  return (
    <CrisisContainer id="crisis">
      <CrisisContent>
        <CrisisTitle>
          The Manufacturing Crisis is Real
        </CrisisTitle>
        
        <CrisisSubtitle>
          While your competitors struggle with outdated systems, you can transform 
          operational challenges into competitive advantages through strategic IT/OT convergence.
        </CrisisSubtitle>

        <StatisticsGrid>
          <StatCard>
            <StatNumber>$260K</StatNumber>
            <StatLabel>Cost per Hour</StatLabel>
            <StatDescription>
              Average manufacturing downtime expense that cripples profitability 
              and destroys competitive positioning
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatNumber>92.5%</StatNumber>
            <StatLabel>Lack Integration</StatLabel>
            <StatDescription>
              Mid-market manufacturers without proper IT/OT convergence, 
              leaving critical vulnerabilities exposed
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatNumber>85%</StatNumber>
            <StatLabel>Improvement Achieved</StatLabel>
            <StatDescription>
              Proven results from our A-SAFE RackEye implementation: 
              warehouse accidents reduced globally
            </StatDescription>
          </StatCard>
        </StatisticsGrid>

        <UrgencyBanner>
          <UrgencyText>
            Industry 5.0 transformation is accelerating. 
            Your window to establish competitive advantage is narrowing rapidly.
          </UrgencyText>
        </UrgencyBanner>
      </CrisisContent>
    </CrisisContainer>
  );
};

export default CrisisResponseSection;